import { useState, useRef, useEffect } from 'react';

export interface AdminUser {
  id: number;
  slackUserId: string;
  ownerEmail: string | null;
}

export function UserMultiSelect({
  users,
  selectedIds,
  onChange,
}: {
  users: AdminUser[];
  selectedIds: Set<number>;
  onChange: (ids: Set<number>) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const toggle = (id: number) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(next);
  };

  const selectAll = () => onChange(new Set(users.map((u) => u.id)));
  const selectNone = () => onChange(new Set());

  const label =
    selectedIds.size === 0
      ? 'Select users...'
      : selectedIds.size === users.length
        ? `All ${users.length} users`
        : `${selectedIds.size} user${selectedIds.size === 1 ? '' : 's'} selected`;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center justify-between w-full rounded-lg border border-border-primary bg-bg-raised px-3 py-2 text-sm text-content-tertiary hover:border-border-primary transition-colors"
      >
        <span>{label}</span>
        <svg className={`w-4 h-4 text-icon-subtle transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="absolute z-10 mt-1 w-full max-h-60 overflow-y-auto rounded-lg border border-border-secondary bg-bg-raised shadow-lg">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-border-tertiary">
            <button type="button" onClick={selectAll} className="text-xs text-content-action hover:text-content-action">
              Select all
            </button>
            <span className="text-icon-subtle">|</span>
            <button type="button" onClick={selectNone} className="text-xs text-icon-subtle hover:text-content-tertiary">
              Clear
            </button>
          </div>
          {users.map((u) => (
            <label
              key={u.id}
              className="flex items-center gap-2.5 px-3 py-1.5 hover:bg-bg-secondary cursor-pointer"
            >
              <input
                type="checkbox"
                checked={selectedIds.has(u.id)}
                onChange={() => toggle(u.id)}
                className="rounded border-border-primary text-content-action focus:ring-indigo-500"
              />
              <span className="text-sm text-content-tertiary truncate">{u.ownerEmail ?? u.slackUserId}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
